import { inject, Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpContextToken } from '@angular/common/http';
import { Observable } from 'rxjs';
import { FilterService } from '@services/filter.service';

/**
 * Context token used to specify an array of global filter keys to be injected into the request body.
 * * @usage
 * ```typescript
 * this.http.post('/api/data', {}, {
 * context: new HttpContext().set(REQ_BODY_GLOBAL_FILTERS, ['selectedGroups', 'vehicleIds'])
 * }).subscribe();
 * ```
 */
export const REQ_BODY_GLOBAL_FILTERS = new HttpContextToken<string[]>(() => []);

/**
 * Interceptor responsible for appending global filters to the HTTP request body.
 * When global filters are applied, the request is automatically converted to the POST method.
 */
@Injectable()
export class GlobalFilterInterceptor implements HttpInterceptor {
  private _filterService = inject(FilterService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const globalFiltersList = request.context.get(REQ_BODY_GLOBAL_FILTERS);

    if (globalFiltersList?.length) {
      return this.handleBodyGlobalFilters(request, next, globalFiltersList);
    }

    return next.handle(request);
  }

  private handleBodyGlobalFilters(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    filterKeys: string[],
  ): Observable<HttpEvent<unknown>> {
    const temporaryFilters = this._filterService.temporaryFilters.getValue();
    const filters = temporaryFilters ?? this._filterService.filters.getValue();

    const bodyPayload: Record<string, any> = {};

    filterKeys.forEach((key) => {
      if (filters && filters[key] !== undefined && filters[key] !== null) {
        switch (key) {
          case 'selectedGroups':
            bodyPayload.groupIds = filters[key];
            break;
          default:
            bodyPayload[key] = filters[key];
            break;
        }
      }
    });

    const currentBody = (request.body as Record<string, any>) || {};

    const modifiedRequest = request.clone({
      method: 'POST',
      body: {
        ...currentBody,
        ...bodyPayload,
      },
    });

    return next.handle(modifiedRequest);
  }
}
